import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

type Cell = boolean | string;
type Row = { label: string; cells: [Cell, Cell, Cell, Cell] };

const cols = ["SendFeedback.ai", "Culture Amp", "Lattice", "Officevibe"];

const rows: Row[] = [
  { label: "Truly anonymous (PII stripped at DB level)", cells: [true, "Partial", "Partial", "Partial"] },
  { label: "Conversational AI follow-ups", cells: [true, false, false, false] },
  { label: "Auto theme & sentiment clustering", cells: [true, true, "Limited", false] },
  { label: "Retention risk scoring", cells: [true, "Add-on", true, false] },
  { label: "Always-on, not just quarterly", cells: [true, false, false, true] },
  { label: "Setup in under 5 minutes", cells: [true, false, false, true] },
  { label: "Flat pricing (no per-seat fees)", cells: [true, false, false, false] },
  { label: "Built for HR (not engineering)", cells: [true, true, "Mixed", true] },
];

const Mark = ({ v }: { v: Cell }) => {
  if (v === true)
    return <Check className="h-5 w-5 text-[hsl(var(--mkt-primary))] mx-auto" aria-label="Yes" />;
  if (v === false)
    return <X className="h-5 w-5 text-muted-foreground/50 mx-auto" aria-label="No" />;
  return <span className="text-sm text-muted-foreground">{v}</span>;
};

export const ComparisonTable = () => {
  return (
    <section className="container mx-auto px-3 lg:px-8 py-16 lg:py-24">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--mkt-primary))] mb-3">
          Why not just use…
        </div>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight">
          The Honest Comparison
        </h2>
        <p className="mt-4 text-muted-foreground">
          What you actually get vs. the workarounds most HR teams cobble together.
        </p>
      </div>

      <div className="max-w-5xl mx-auto rounded-3xl bg-card border border-border mkt-shadow-card overflow-hidden">
        <div className="grid grid-cols-5 bg-muted/60">
          <div className="px-6 py-5 text-sm font-semibold text-muted-foreground">Capability</div>
          {cols.map((c, i) => (
            <div
              key={c}
              className={cn(
                "px-6 py-5 text-center text-sm font-bold",
                i === 0 ? "text-[hsl(var(--mkt-primary-deep))]" : "text-foreground/70"
              )}
            >
              {c}
            </div>
          ))}
        </div>
        {rows.map((r, idx) => (
          <div
            key={r.label}
            className={cn(
              "grid grid-cols-5 items-center",
              idx !== rows.length - 1 && "border-b border-border"
            )}
          >
            <div className="px-6 py-4 text-sm font-medium text-foreground">{r.label}</div>
            {r.cells.map((c, i) => (
              <div
                key={i}
                className={cn(
                  "px-6 py-4 text-center",
                  i === 0 && "bg-[hsl(var(--mkt-primary)/0.04)]"
                )}
              >
                <Mark v={c} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};
